import { motion } from "framer-motion";
import { Droplets, Clock, Zap, Phone } from "lucide-react";
import { IMAGES, BUSINESS } from "../lib/constants";

const POINTS = [
  {
    icon: Droplets,
    title: "Squeegee-detailed, edge to edge",
    body: "No wipes, no shortcuts. Every pane worked by hand until the corners give up nothing.",
  },
  {
    icon: Clock,
    title: "Arrival windows, confirmed by text",
    body: "You pick the day and the window. I confirm by text and show up when I said I would.",
  },
  {
    icon: Zap,
    title: "Pay with Zelle — no invoices",
    body: "Your total is locked before I arrive. Send it by Zelle and we're done.",
  },
];

export default function Standard() {
  return (
    <section className="relative py-24 sm:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div
            className="relative h-[420px] sm:h-[520px] overflow-hidden rounded-3xl border border-white/10"
            style={{ boxShadow: "0 60px 120px -50px rgba(0,0,0,0.95)" }}
            data-testid="standard-frame"
          >
            <img
              src={IMAGES.uniform}
              alt="Window cleaner in a plain baby blue t-shirt with squeegee over shoulder"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-midnight/70 via-transparent to-transparent" />
          </div>
          <span className="absolute -top-2 -left-2 w-8 h-8 border-t-2 border-l-2 border-crystal/60 rounded-tl-xl" />
          <span className="absolute -bottom-2 -right-2 w-8 h-8 border-b-2 border-r-2 border-crystal/60 rounded-br-xl" />
        </motion.div>

        <div>
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="w-9 h-[1px] bg-crystal" />
              <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-crystal">The Standard</span>
            </div>
            <h2 className="font-display font-medium text-[clamp(2.2rem,4.5vw,3.8rem)] leading-[1.02] text-white tracking-tight">
              Locally owned.
              <br />
              <em className="italic text-crystal">One pane at a time.</em>
            </h2>
          </motion.div>

          <div className="mt-10 space-y-8">
            {POINTS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="flex gap-5"
              >
                <span className="shrink-0 rounded-xl border border-crystal/30 bg-crystal/[0.07] p-3 h-fit">
                  <p.icon size={18} className="text-crystal" />
                </span>
                <div>
                  <h3 className="font-display text-2xl text-white">{p.title}</h3>
                  <p className="mt-1.5 text-mist text-sm leading-relaxed max-w-md">{p.body}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.a
            href={BUSINESS.phoneHref}
            data-testid="standard-phone-link"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 inline-flex items-center gap-3 rounded-full border border-white/20 px-7 py-4 font-mono text-[11px] tracking-[0.25em] uppercase text-white hover:border-crystal hover:text-crystal transition-colors duration-200"
          >
            <Phone size={14} /> Questions? {BUSINESS.phone}
          </motion.a>
        </div>
      </div>
    </section>
  );
}
